import React, { useEffect, useCallback, useState } from 'react'
import { Layout, Space, message, theme, Dropdown, Menu } from 'antd'
import { Header } from 'antd/es/layout/layout'
import { batch, useDispatch, useSelector } from 'react-redux'
import { isSignInWithEmailLink, onAuthStateChanged, signInWithEmailLink } from 'firebase/auth'
import { useNavigate, useLocation } from 'react-router-dom'
import { BrowserView, MobileView, deviceDetect } from 'react-device-detect'

import AuthEnums from '../../custom/Auth/utils/enums'
import authReducer from '../../custom/Auth/utils/reducer'
import { firebaseAuth } from '../..'
import { findAgiliteUser, updateAgiliteUser } from '../../custom/Auth/utils/utils'
import { userHasPassphrase } from '../../custom/Auth/utils/passphraseUtils'
import { saveAuthState, getCachedAuthState, clearAuthState } from '../../custom/Auth/utils/authPersistence'
import coreReducer from '../utils/reducer'
import { leftMenuItems } from '../../setup'

// Components
import DashboardWrapper from '../../custom/Admin/dashboard/components/DashboardWrapper'
import EmailSignUpForm from '../../custom/Auth/components/FormComponents/emailSignUpForm'
import PassphraseSetupModal from '../../custom/Auth/components/PassphraseSetupModal'
import { readServicesPwa } from '../../custom/Admin/services/utils/utils'
import { readClinicsPwa } from '../../custom/Admin/clinics/utils/utils'
import clinicsCRUDReducer from '../../custom/Admin/clinics/utils/reducer'
import { readConfigPwa } from '../../custom/Admin/config/utils/utils'
import { readTermsPwa } from '../../custom/Admin/terms-conditions/utils/utils'
import servicesReducer from '../../custom/Admin/services/utils/reducer'
import { readProfessionsPwa, readRolesPwa } from '../utils/utils'

import CoreEnums from '../utils/enums'
import { handleError } from '../../custom/lib/utils'
import { readVirtualServicesPwa } from '../../custom/Admin/virutal-services/utils/utils'
import virtualServicesReducer from '../../custom/Admin/virutal-services/utils/reducer'
import { getPatientPwa } from '../../custom/Medical History/utils/utils'
import MetaIconLogo from '../../assets/images/landing-image.png'
import { readSystemUsersAggregate } from '../../custom/Admin/system-users/utils/utils'
import DependentsReducer from '../../custom/Profile/components/dependants/utils/depentdents-reducer'

import './toolsbar.css'

// JWT
import { isExpired, decodeToken } from 'react-jwt'
import { MenuOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHome, faHeartPulse, faUser, faBars } from '@fortawesome/free-solid-svg-icons'
import { isPatient } from '../../custom/lib/profile-utils'
import { checkAndFixDatabase } from '../../db/indexedDB'

const Toolbar = (props) => {
  const coreState = useSelector((state) => state.core)
  const authState = useSelector((state) => state.auth)
  const appTitle = `${useSelector((state) => state.core.title) || 'App'} Portal`.toUpperCase()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  // Mobile burger menu state
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false)

  // Mobile navigation menu items
  const mobileMenuItems = [
    {
      key: CoreEnums.tabKeys.HOME,
      icon: <FontAwesomeIcon icon={faHome} style={{ marginRight: '8px', color: '#10b2e1' }} />,
      label: 'Home'
    },
    {
      key: CoreEnums.tabKeys.PAT_HEALTH_STATS,
      icon: <FontAwesomeIcon icon={faHeartPulse} style={{ marginRight: '8px', color: '#10b2e1' }} />,
      label: 'Health'
    },
    {
      key: CoreEnums.tabKeys.PAT_PATIENT_PROFILE,
      icon: <FontAwesomeIcon icon={faUser} style={{ marginRight: '8px', color: '#10b2e1' }} />,
      label: 'Profile'
    }
  ]

  // Handle mobile navigation selection
  const handleMobileMenuClick = ({ key }) => {
    dispatch(coreReducer.actions.menuItemClick({ key: key }))
    setMobileMenuVisible(false)
  }

  const handleDependencies = useCallback(
    async (agiliteUser) => {
      try {
        const [
          rolesResponse,
          professionsResponse,
          servicesResponse,
          virtualServicesResponse,
          clinicsResponse,
          termsResponse,
          configResponse,
          medicalHistoryResponse,
          // subscriptionsResponse,
          dependantsResponse
        ] = await Promise.all([
          readRolesPwa(),
          readProfessionsPwa(),
          readServicesPwa({ isActive: true }),
          readVirtualServicesPwa({ isActive: true }),
          readClinicsPwa({ isActive: true }),
          readTermsPwa(),
          readConfigPwa(),
          getPatientPwa(),
          // agiliteUser && agiliteUser._id ? getSubscriptions({ userRef: agiliteUser._id }) : undefined,
          agiliteUser && agiliteUser._id
            ? readSystemUsersAggregate({ mainMemberId: agiliteUser._id }, null, null, null, null, true)
            : undefined
        ])

        dispatch(authReducer.actions.setRoles(rolesResponse))
        dispatch(authReducer.actions.setProfessions(professionsResponse))
        dispatch(servicesReducer.actions.setRecords(servicesResponse))
        dispatch(virtualServicesReducer.actions.setRecords(virtualServicesResponse))
        dispatch(clinicsCRUDReducer.actions.setRecords(clinicsResponse))
        dispatch(authReducer.actions.setTerms(termsResponse))
        // dispatch(SubscriptionsReducer.actions.setSubscriptions(subscriptionsResponse))
        dispatch(DependentsReducer.actions.setRecords(dependantsResponse))

        if (configResponse.length > 0) {
          const config = configResponse[0]
          dispatch(authReducer.actions.setConfig(config))
        } else {
          message.warning('No Configurations found')
        }
      } catch (e) {
        message.error(handleError(e, true))
      }
    },
    [dispatch]
  )

  const checkOnlineStatus = useCallback(async () => {
    if (!navigator.onLine) {
      dispatch(coreReducer.actions.setOnline(false))
      return false
    }

    try {
      // Use a lightweight ping endpoint if available
      const response = await fetch(`${process.env.REACT_APP_AGILITE_API_URL}/ping`, {
        method: 'HEAD',
        cache: 'no-cache',
        // Add a reasonable timeout to prevent hanging requests
        signal: AbortSignal.timeout(5000)
      })

      dispatch(coreReducer.actions.setOnline(response.ok))
      return response.ok
    } catch (error) {
      dispatch(coreReducer.actions.setOnline(false))
      return false
    }
  }, [dispatch])

  useEffect(() => {
    let interval = 90000
    let isActive = true

    // Check immediately on mount
    checkOnlineStatus()

    // Set up periodic checking
    const intervalId = setInterval(() => {
      if (isActive) {
        checkOnlineStatus()
      }
    }, interval)

    // Listen to browser's online/offline events
    const handleOnline = () => {
      if (isActive) {
        checkOnlineStatus()
      }
    }

    const handleOffline = () => {
      if (isActive) {
        dispatch(coreReducer.actions.setOnline(false))
      }
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Cleanup
    return () => {
      isActive = false
      clearInterval(intervalId)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [dispatch, checkOnlineStatus])

  const initializeApp = async (user, agiliteUser = null) => {
    try {
      const entity = coreState.entity
      let providerId = null

      // First Fix all databases
      // We need to do this because the DB Version might have changed then we need to delete the old DB and create a new one
      await checkAndFixDatabase()

      if (!['/checkin', '/payment-status'].includes(window.location.pathname)) {
        dispatch(authReducer.actions.setLoading(true))

        if (user) {
          dispatch(authReducer.actions.setUser(user))
          dispatch(authReducer.actions.setLoggedIn(true))

          if (!agiliteUser) {
            providerId = firebaseAuth.currentUser.providerData[0].providerId

            if (providerId === AuthEnums.signInTypes.GOOGLE || providerId === AuthEnums.signInTypes.PASSWORD) {
              agiliteUser = user.phoneNumber
                ? await findAgiliteUser({ phoneNumber: user.phoneNumber })
                : await findAgiliteUser({ email: user.email })
            } else {
              if (user.phoneNumber) {
                agiliteUser = await findAgiliteUser({ phoneNumber: user.phoneNumber })
              }
            }
          }

          if (agiliteUser) {
            if (
              entity &&
              agiliteUser.extraData.role.type !== 'patient' &&
              agiliteUser.extraData.role.type !== 'super_admin' &&
              !agiliteUser.extraData.entities.includes(entity._id)
            ) {
              dispatch(authReducer.actions.setUser(null))
              dispatch(authReducer.actions.setLoggedIn(false))
              await clearAuthState()
              throw new Error(CoreEnums.errorMessages.ENTITY_NOT_AUTH)
            }

            if (!agiliteUser.extraData?.isActive) {
              await clearAuthState()
              throw new Error('Account Disabled')
            }
          }

          if (!agiliteUser || !agiliteUser.idNo) {
            dispatch(authReducer.actions.setCompleteEmailSignUp(true))
            if (agiliteUser) {
              dispatch(authReducer.actions.setAgiliteUser(agiliteUser))
              await saveAuthState(user, agiliteUser)
            }
          } else {
            dispatch(authReducer.actions.setAgiliteUser(agiliteUser))
            await saveAuthState(user, agiliteUser)
          }

          // Enhanced invite status check
          if (
            agiliteUser &&
            (agiliteUser.extraData.inviteStatus === false ||
              agiliteUser.extraData.inviteStatus === null ||
              agiliteUser.extraData.inviteStatus === undefined)
          ) {
            console.log('🔍 Setting completeEmailSignUp = true, inviteStatus:', agiliteUser.extraData.inviteStatus)
            dispatch(authReducer.actions.setCompleteEmailSignUp(true))
          } else {
            console.log('❌ NOT setting completeEmailSignUp, inviteStatus:', agiliteUser?.extraData?.inviteStatus)
          }

          dispatch(authReducer.actions.setLoading(false))

          if (agiliteUser) {
            // Check if user needs to set up a passphrase (for existing users)
            if (!userHasPassphrase(agiliteUser) && !authState.passphraseSetupRequired) {
              console.log('🔐 User does not have a backup passphrase set up, prompting setup')
              dispatch(authReducer.actions.setPassphraseSetupRequired(true))
            }

            if (agiliteUser.extraData.role.type === 'admin' || agiliteUser.extraData.role.type === 'reception') {
              if (agiliteUser.extraData.role.type === 'admin') {
                dispatch(coreReducer.actions.enableDisableRightMenu(true))
              }

              if (deviceDetect().isBrowser) {
                dispatch(coreReducer.actions.hideTabs(false))
              } else {
                dispatch(coreReducer.actions.hideTabs(true))
              }
            } else {
              if (agiliteUser.extraData.role.type !== 'medical_professional') {
                dispatch(coreReducer.actions.enableDisableLeftMenu(true))
                dispatch(coreReducer.actions.hideTabs(true))
              }
            }

            if (process.env.REACT_APP_ENVIRONMENT === 'dev') {
              dispatch(coreReducer.actions.enableDisableRightMenu(true))
            }
          }

          handleDependencies(agiliteUser)

          dispatch(coreReducer.actions.enableDisableTabs(true))
          dispatch(coreReducer.actions.setRootContent(DashboardWrapper))

          if (agiliteUser && agiliteUser.extraData.role.type === 'patient') {
            if (!agiliteUser.extraData.entities.includes(entity._id)) {
              await updateAgiliteUser(agiliteUser._id, {
                ...agiliteUser,
                extraData: {
                  ...agiliteUser.extraData,
                  entities: [...agiliteUser.extraData.entities, entity._id]
                }
              })
            }
          }
        } else {
          dispatch(authReducer.actions.setLoading(false))
          dispatch(authReducer.actions.setUser(null))
          dispatch(authReducer.actions.setLoggedIn(false))
          await clearAuthState()
        }
      }
    } catch (e) {
      message.error(handleError(e, true))
      dispatch(authReducer.actions.setLoading(false))
    }
  }

  useEffect(() => {
    async function init() {
      let email = null

      try {
        const isOnline = coreState.online

        if (isOnline && isSignInWithEmailLink(firebaseAuth, window.location.href)) {
          email =
            window.localStorage.getItem(AuthEnums.localStorage.EMAIL_FOR_SIGN_IN) ||
            window.prompt('Please provide your email for confirmation')
          await signInWithEmailLink(firebaseAuth, email, window.location.href)
          window.localStorage.removeItem(AuthEnums.localStorage.EMAIL_FOR_SIGN_IN)
          const pathArray = window.location.pathname.split('/').filter((p) => p !== '') || []
          navigate(`/${pathArray[0]}${AuthEnums.routes.ROOT}`)
        }

        // If offline, try to use cached auth state
        if (!isOnline) {
          const cachedAuth = await getCachedAuthState()

          if (cachedAuth?.firebaseUser && cachedAuth?.agiliteUser) {
            initializeApp(cachedAuth.firebaseUser, cachedAuth.agiliteUser)
            return
          } else {
            message.error('You are offline and no cached session was found. Please check your internet connection.')
            return
          }
        } else {
          onAuthStateChanged(
            firebaseAuth,
            async (firebaseUser) => {
              initializeApp(firebaseUser)
            },
            (error) => {
              console.error(error)
            }
          )
        }
      } catch (e) {
        message.error(handleError(e, true))
        dispatch(authReducer.actions.setLoading(false))
      }
    }

    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token')
    let decodedToken = null
    let isExpiredToken = null

    if (token) {
      decodedToken = decodeToken(token)
      isExpiredToken = isExpired(token)
    }

    if (coreState.entity) {
      if (!isExpiredToken && decodedToken && decodedToken?.userRef) {
        // When we get here, we know that the app is loaded in an iFrame for Glovent
        handleAutoSignIn(decodedToken.userRef)
        dispatch(coreReducer.actions.setSignOutDisabled(true))
        dispatch(coreReducer.actions.setMobNavBarEnabled(false))
      } else {
        init()
      }
    }

    // eslint-disable-next-line
  }, [coreState.entity])

  const handleAutoSignIn = async (userRef) => {
    dispatch(authReducer.actions.setLoading(true))
    try {
      const agiliteUser = await findAgiliteUser({ _id: userRef })

      if (agiliteUser) {
        dispatch(authReducer.actions.setLoggedIn(true))
        dispatch(authReducer.actions.setUser(agiliteUser))
        dispatch(authReducer.actions.setAgiliteUser(agiliteUser))
        dispatch(coreReducer.actions.enableDisableTabs(true))
        dispatch(coreReducer.actions.enableDisableLeftMenu(true))
        dispatch(coreReducer.actions.hideTabs(true))
        dispatch(coreReducer.actions.setRootContent(DashboardWrapper))
        handleDependencies(agiliteUser)
      }
    } catch (e) {
      message.error(handleError(e, true))
    }
    dispatch(authReducer.actions.setLoading(false))
  }

  const getActiveTabTitle = useCallback(() => {
    const tabKeysEnum = CoreEnums.tabKeys
    const titleEnum = CoreEnums.tabTitles

    const activeKey = coreState.tabNavigation?.activeKey

    // Ensure activeKey is a string before using string methods
    if (!activeKey || typeof activeKey !== 'string') {
      return 'Title Not Found'
    }

    const key =
      Object.keys(tabKeysEnum).find((k) => tabKeysEnum[k] === activeKey) ||
      Object.keys(tabKeysEnum).find((k) => {
        const splitKey = activeKey && typeof activeKey === 'string' ? activeKey.split('_')[0] : ''
        return tabKeysEnum[k] === splitKey
      })

    return key ? titleEnum[key] : 'Title Not Found'
  }, [coreState.tabNavigation])

  const getMenuColor = () => {
    const tabKeysEnum = CoreEnums.tabKeys
    const menuColorsEnum = CoreEnums.menuColors

    const activeKey = coreState.tabNavigation?.activeKey

    // Ensure activeKey is a string before using string methods
    if (!activeKey || typeof activeKey !== 'string') {
      return menuColorsEnum.DEFAULT
    }

    const key = Object.keys(tabKeysEnum).find((k) => {
      if (tabKeysEnum[k] === activeKey) return true
      const splitKey = activeKey && typeof activeKey === 'string' ? activeKey.split('_')[0] : ''
      return tabKeysEnum[k] === splitKey
    })

    return key ? menuColorsEnum[key] : menuColorsEnum.DEFAULT
  }

  const getActiveTabIcon = useCallback(() => {
    const tabKeysEnum = CoreEnums.tabKeys
    const activeKey = coreState.tabNavigation?.activeKey

    // Ensure activeKey is a string before using string methods
    if (!activeKey || typeof activeKey !== 'string') {
      return null
    }

    // Get the menu items and find the matching icon
    const menuItems = leftMenuItems()
    let matchedItem = menuItems.find((item) => item.key === activeKey)

    if (!matchedItem) {
      // Try to match with split key for dynamic tabs like patient file viewer
      const splitKey = activeKey.split('_')[0]
      matchedItem = menuItems.find((item) => item.key === splitKey)
    }

    // Clone the icon and remove inline styles that might override our CSS
    if (matchedItem?.icon) {
      return React.cloneElement(matchedItem.icon, {
        style: {} // Remove any inline styles that might interfere
      })
    }

    return null
  }, [coreState.tabNavigation])

  const { token } = theme.useToken()

  return (
    <Layout
      style={
        authState.loggedIn
          ? { margin: 0, position: 'sticky', top: 0, zIndex: 10 }
          : { margin: 0, display: 'none', position: 'sticky', top: 0, zIndex: 10 }
      }
    >
      <Header
        style={{
          background:
            deviceDetect().isMobile && authState?.agiliteUser?.extraData?.role?.type === 'patient' ? 'white' : '',
          fontSize: '13pt',
          color: 'white',
          paddingLeft: 20,
          paddingRight: 20,
          boxShadow: deviceDetect().isMobile ? '0 2px 8px rgba(0,0,0,0.15)' : 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
              <MobileView>
                <img src={MetaIconLogo} style={{ height: 30, display: 'block' }} alt='Meta-Health Logo' />
              </MobileView>
              {props.leftMenuEnabled && !deviceDetect().isBrowser && !isPatient() ? (
                <MenuOutlined
                  style={{ float: 'left', cursor: 'pointer', fontSize: 24 }}
                  onClick={props.onLeftMenuOpen}
                />
              ) : null}
              <BrowserView>
                <div
                  style={{
                    width: coreState.sideBarExpanded ? 275 : 80,
                    transition: 'all 300ms ease',
                    height: '100%',
                    top: 0,
                    left: 0,
                    position: 'absolute',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingLeft: coreState.sideBarExpanded ? 8 : 0,
                    boxSizing: 'content-box',
                    background: '#2A3649',
                    minWidth: coreState.sideBarExpanded ? 275 : 80,
                    maxWidth: coreState.sideBarExpanded ? 275 : 80
                  }}
                >
                  {coreState.sideBarExpanded ? (
                    <center>
                      <b
                        style={{
                          margin: 0,
                          height: '100%',
                          width: '100%'
                        }}
                      >
                        {authState?.agiliteUser?.extraData?.role.type === 'super_admin' ? 'SUPER ADMIN' : appTitle}
                      </b>
                    </center>
                  ) : (
                    <img
                      src={coreState?.entity?.entityIcon}
                      style={{ width: 40, display: 'block' }}
                      alt='Meta-Health Logo'
                    />
                  )}
                </div>
              </BrowserView>
              <MobileView>
                <b className='TITLE_OVERIDE'>{getActiveTabTitle()}</b>
              </MobileView>
            </div>
            <BrowserView>
              <b
                className='TITLE_OVERIDE'
                style={{
                  left: coreState.sideBarExpanded ? 275 : 80,
                  transition: 'left 300ms ease'
                }}
              >
                {getActiveTabIcon() && <span className='tab-icon'>{getActiveTabIcon()}</span>}
                {getActiveTabTitle()}
              </b>
            </BrowserView>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {location.pathname !== '/checkin' || authState.loggedIn ? props.customMenus.content : null}

              {/* Mobile Burger Menu - Only for patients */}
              <MobileView>
                {isPatient() && (
                  <Dropdown
                    menu={{
                      items: mobileMenuItems,
                      onClick: handleMobileMenuClick
                    }}
                    trigger={['click']}
                    placement='bottomRight'
                    visible={mobileMenuVisible}
                    onVisibleChange={setMobileMenuVisible}
                    overlayClassName='mobile-burger-menu'
                  >
                    <button
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        color: deviceDetect().isMobile ? '#333' : '#fff'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.1)'
                        e.target.style.opacity = '0.7'
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)'
                        e.target.style.opacity = '1'
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faBars}
                        style={{
                          fontSize: '20px',
                          color: deviceDetect().isMobile ? '#333' : '#fff'
                        }}
                      />
                    </button>
                  </Dropdown>
                )}
              </MobileView>
            </div>
          </div>
        </div>
      </Header>
      <EmailSignUpForm />
      <PassphraseSetupModal
        visible={authState.passphraseSetupRequired}
        onCancel={() => {
          // Users cannot skip passphrase setup - this should not be called
          // but keeping for component compatibility
        }}
        onSuccess={() => {
          dispatch(authReducer.actions.setPassphraseSetupRequired(false))
        }}
      />
    </Layout>
  )
}

export default Toolbar
